import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import type { TypeGetReceivingReportHistoriesReportIdResponse } from '~/types/receive.types';

type TypeGetReportHistoriesByReportIdParams = {
  ownerCode: string;
  reportId: string;
};

const fetchReportHistoriesByReportId = async ({
  ownerCode,
  reportId,
}: TypeGetReportHistoriesByReportIdParams): Promise<TypeGetReceivingReportHistoriesReportIdResponse> => {
  const { data } = await axiosInstance.get<TypeGetReceivingReportHistoriesReportIdResponse>(
    `${API_ENDPOINT.RECEIVE.GET_REPORT_HISTORIES_REPORT_DETAIL}/${ownerCode}/report/histories/${reportId}`
  );
  return data;
};

/**
 * [입고관리 > 입고 리포트 관리] 입고 리포트 상세 조회 Mutation Hook
 */
export const useGetReportHistoriesByReportIdMutation = () => {
  return useMutation({
    mutationFn: fetchReportHistoriesByReportId,
  });
};

// 한 응답에 COMPLETED + 코멘트 3종(comment/completedComment/closedComment) + tasks 2건(이미지 포함) + 처리완료 이미지 분기를 모두 포함
const normalDetailResponse: TypeGetReceivingReportHistoriesReportIdResponse = {
  data: {
    report: {
      reportCode: 'RP20260513_00001',
      requestCode: 'OR20260513_00001',
      reportStatus: 'COMPLETED',
      receivingRequestStatus: 'COMPLETED',
      supplierName: '테스트 화주사_첫번째 공급사',
      statusUpdatedAt: '2026-05-13T17:00:00',
      createdAt: '2026-05-13T11:00:00',
      comment: '리포트 발행 사유: 일부 상품 파손 확인 필요',
      completedComment: '파손 상품 별도 처리 후 입고 완료',
      closedComment: null,
      sku: 5,
      unit: 110,
      requestSku: 5,
      requestUnit: 120,
      receivingExpectedDate: '2026-05-13',
      receivingExpectedTime: '10:00~12:00',
      receivedDateTime: '2026-05-13T16:30:00',
    },
    tasks: [
      {
        taskName: '벌크해체',
        goodsCode: 'EN0000010151',
        goodsName: '닥터유 단백질 35g(3PL)',
        sku: 3,
        unit: 60,
        taskUnit: 60,
        taskMeasure: '박스',
        images: [{ filePath: 'https://placehold.co/200x200/png?text=task1' }],
      },
      {
        taskName: '스티커부착',
        goodsCode: 'EN0000010152',
        goodsName: '닥터유 단백질 40g(3PL)',
        sku: 2,
        unit: 50,
        taskUnit: 50,
        taskMeasure: '개',
        images: [
          { filePath: 'https://placehold.co/200x200/png?text=task2-a' },
          { filePath: 'https://placehold.co/200x200/png?text=task2-b' },
        ],
      },
    ],
    images: [
      { filePath: 'https://placehold.co/200x200/png?text=done1' },
      { filePath: 'https://placehold.co/200x200/png?text=done2' },
    ],
  },
};

// 발행취소 — CANCELED 뱃지 + closedComment 분기 + completedComment/images 부재 케이스
const canceledDetailResponse: TypeGetReceivingReportHistoriesReportIdResponse = {
  data: {
    report: {
      reportCode: 'RP20260513_00002',
      requestCode: 'OR20260513_00002',
      reportStatus: 'CANCELED',
      receivingRequestStatus: 'CANCELED',
      supplierName: '테스트 화주사_두번째 공급사',
      statusUpdatedAt: '2026-05-13T15:00:00',
      createdAt: '2026-05-13T12:00:00',
      comment: '리포트 발행 사유: 상품 수량 부족 확인',
      completedComment: null,
      closedComment: '발행 취소 사유: 공급사 재입고 일정 조정으로 리포트 회수',
      sku: 2,
      unit: 30,
      requestSku: 2,
      requestUnit: 30,
      receivingExpectedDate: '2026-05-13',
      receivingExpectedTime: '13:00~15:00',
      receivedDateTime: null,
    },
    tasks: [
      {
        taskName: '벌크해체',
        goodsCode: 'EN0000010153',
        goodsName: '닥터유 단백질 50g(3PL)',
        sku: 2,
        unit: 30,
        taskUnit: 30,
        taskMeasure: '박스',
        images: [],
      },
    ],
    images: [],
  },
};

registerMockAPI<TypeGetReceivingReportHistoriesReportIdResponse>({
  method: 'get',
  endpoint: '/api-inbound/v1/3pl/receiving/:ownerCode/report/histories/:reportId',
  // 입고리포트 상세와 입고현황3PL 상세 모달이 같은 리포트 상세 엔드포인트를 호출하므로 두 자리에 진열한다.
  label: ['입고/입고리포트/상세', '입고/입고현황3PL/상세'],
  scenarios: [
    { name: '정상 응답 — 처리완료 + 코멘트 3종 + tasks 2건 + 이미지 혼합', response: normalDetailResponse },
    { name: '정상 응답 — 발행취소 + 발행 코멘트 + 취소 코멘트', response: canceledDetailResponse },
    {
      name: '잘못된 파라미터 (9001)',
      response: { error: { code: 9001, message: 'Error Occured By Not Valid Parameters' } },
      status: 400,
    },
  ],
});
